import { useCallback, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES, IDimensionalityOption } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce, isEqual } from 'lodash';
import {
  IGetPersonalExpensesCalculationsByProductParams,
  useGetPersonalExpensesCalculationsOnCategory,
  useSentPersonalExpensesByProduct,
} from 'services/calculations/personalExpenses';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import { IRow } from 'components/BusinessToolForm/Sections/FinancialPlan/PlannedExpenses/CostOfSalesForm/utils/form';

import ToastService from 'helpers/ToastService';

import {
  createPersonalExpensesByCategoryRequestParam,
  createPersonalExpensesByProductRequestParam,
  createPersonalExpensesTable,
  getUpdatedPersonalExpensesTotal,
} from './helper';
import { unitConvertorMultiplier } from '../../helpers/balanceSheet/consts';
import { IFormContentTypes, observedFields } from '../../utils/form';

export const usePersonalExpensesCalculation = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getPersonalExpensesCalculation = useGetPersonalExpensesCalculationsOnCategory();
  const sendPersonalExpensesByProduct = useSentPersonalExpensesByProduct();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const previousSalariesByCategory = useRef<null | IRow[][]>(null);
  const previousSalaryByProduct = useRef<null | [IRow[], IPeriodsTableRow[], IDimensionalityOption]>(null);
  const needToUpdateProductsCost = useRef(false);

  const [personalExpenseTableFieldName, personalExpenseTotalFieldName, cashToEmployeesFieldName] =
    observedFields.personalExpensesByCategory.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(
        async (personalExpenseData, salariesByProductParams, unit, personalExpenseTable, personalExpenseTotal) => {
          const personalExpensesByCategoryParams = {
            unit,
            businessPlanId,
            personalExpenseData,
          };

          try {
            if (needToUpdateProductsCost.current) {
              const productSalariesPromises = salariesByProductParams.map(
                (params: IGetPersonalExpensesCalculationsByProductParams) =>
                  sendPersonalExpensesByProduct.mutateAsync(params),
              );
              await Promise.all(productSalariesPromises);
              needToUpdateProductsCost.current = false;
            }

            const { totalVariablePayroll, variableWeightedAvg, totalAnnualExpGrowth, periodWiseTotalPayroll } =
              await getPersonalExpensesCalculation.mutateAsync(personalExpensesByCategoryParams);

              const multiplier = unitConvertorMultiplier[DIMENSIONALITY_VALUES.SINGLE][unit]

            const updatedPersonalExpensesTable = createPersonalExpensesTable(
              personalExpenseTable,
              personalExpenseData.personalExpenseData,
              totalVariablePayroll,
              variableWeightedAvg,
              multiplier
            );

            const updatedPersonalExpensesTotal = getUpdatedPersonalExpensesTotal(
              personalExpenseTotal,
              Math.abs(Number(periodWiseTotalPayroll.monthValues.m1)),
              totalAnnualExpGrowth,
            );

            setValue(personalExpenseTableFieldName, updatedPersonalExpensesTable);
            setValue(personalExpenseTotalFieldName, updatedPersonalExpensesTotal);
            setValue(cashToEmployeesFieldName, periodWiseTotalPayroll);
          } catch (error) {
            if (error instanceof Error) ToastService.showError(error.message);
          }
        },
        500,
      ),
     
    [],
  );

  const personalExpensesCalculationHandler = useCallback(
    async (content: IFormContentTypes['personalExpensesByCategory']) => {
      if (!canAccess) return;

      const [
        operationUnit,
        fixedCostOfSalesTable,
        rAndDTable,
        salesAndMarketingTable,
        generalAndAdministrativeTable,
        costOfEachUnitTable,
        products,
        production,
        personalExpenseTable,
        personalExpenseTotal,
      ] = content;
      const salariesArray = [
        fixedCostOfSalesTable,
        generalAndAdministrativeTable,
        salesAndMarketingTable,
        rAndDTable,
      ].map((table) => table.slice(0, 2));

      const costOfEachProductSalaries = costOfEachUnitTable.slice(0, 2);
      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;

      if (!isEqual([costOfEachProductSalaries, production, unit], previousSalaryByProduct.current)) {
        previousSalaryByProduct.current = [costOfEachProductSalaries, production, unit];
        needToUpdateProductsCost.current = true;
      }

      if (isEqual(salariesArray, previousSalariesByCategory.current) && !needToUpdateProductsCost.current) return;

      previousSalariesByCategory.current = salariesArray;
      const personalExpenseData = {
        personalExpenseData: createPersonalExpensesByCategoryRequestParam(salariesArray),
      };

      const salariesByProductParams = createPersonalExpensesByProductRequestParam(
        costOfEachProductSalaries,
        products,
        unit,
        businessPlanId,
      );

      debouncedUpdateFunction(
        personalExpenseData,
        salariesByProductParams,
        unit,
        personalExpenseTable,
        personalExpenseTotal,
      );
    },
     
    [debouncedUpdateFunction],
  );

  return personalExpensesCalculationHandler;
};
